import React, { useState, useRef } from "react";
import { Input, TextArea } from "@jammable/ui-components/Forms";
import { Button, Checkbox, toast } from "@jammable/ui-core";
import * as yup from "yup";
import { useFormik } from "formik";
import useAuthModal from "@/hooks/useAuthModal";
import axios from "axios";
import { requireIdToken } from "../../../utils/firebaseAuth";
import { Spinner } from "@jammable/ui-components/Loader";
import { useRouter } from "next/router";
import UploadImage from "@/components/UploadImage";

const CreateCollectionModal = () => {
  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const { checkAuthAndExecute } = useAuthModal();
  const imageUploadRef = useRef(null);

  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .required("Title is required")
      .max(50, "Maximum 50 characters allowed!"),
    description: yup
      .string()
      .required("Description is required")
      .max(500, "Maximum 500 characters allowed!"),
  });

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        if (!image) {
          toast.error("Please upload an image");
          return false;
        }

        setLoading(true);
        const file = await imageUploadRef.current.getCroppedImageFile();
        const token = await requireIdToken();
        const response = await axios.get("/api/s3/getPresignedUrl", {
          params: { type: "collection" },
          headers: { Authorization: token },
        });
        const { url, absoluteUrl } = response.data;
        const imageOptions = {
          headers: { "Content-Type": file.type },
        };
        await axios.put(url, file, imageOptions);

        const imageUrl = absoluteUrl;
        await axios
          .post(
            `/api/collection/createCollection`,
            {
              ...values,
              image: imageUrl,
            },
            { headers: { Authorization: token } },
          )
          .then((res) => {
            router.push(`/collection/${res.data.collection._id}`);
          })
          .catch((err) => {
            toast.error(err?.response?.data?.message || "Something went wrong");
          });
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    },
    validateOnChange: false,
    validateOnBlur: false,
    validateOnMount: false,
  });

  const handleSubmit = async () => {
    const errors = await formik.validateForm();
    if (Object.keys(errors).length > 0) {
      if (errors.title) {
        toast.error(errors.title);
        return;
      }
      if (errors.description) toast.error(errors.description);
    } else {
      formik.handleSubmit(); // Submit the form if no errors
    }
  };

  return (
    <div>
      <div className="tw-text-xl tw-font-black tw-text-center tw-flex tw-items-center tw-justify-center tw-gap-1">
        Create Collection
      </div>
      <div className="tw-text-center tw-text-sm tw-font-semibold tw-text-black/60">
        A collection is like an album for voices that you'd like to save!
      </div>
      <form
        className="tw-flex tw-flex-col tw-gap-3 tw-mt-4 tw-justify-center tw-items-center "
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <UploadImage ref={imageUploadRef} setImage={setImage} />
        <Input
          {...formik.getFieldProps("title")}
          placeholder="Collection Name"
          className="tw-w-full"
        />
        <TextArea
          {...formik.getFieldProps("description")}
          placeholder="Collection Description"
          className="tw-w-full"
        />
        <div className="tw-flex tw-items-center tw-justify-start tw-w-full tw-gap-2 tw-cursor-pointer tw-bg-white  tw-rounded-2xl">
          <Checkbox checked={formik.values.isPrivate} />
          <div
            onClick={() => {
              formik.setFieldValue("isPrivate", !formik.values.isPrivate);
            }}
          >
            <div className="tw-text-sm tw-font-semibold">
              Make Collection Private
            </div>
            <div className="tw-text-xs tw-text-black/40">
              Make your collection private to only you!
            </div>
          </div>
        </div>

        <Button
          disabled={loading}
          type="submit"
          className="tw-rounded-full tw-w-full tw-mt-1"
        >
          {loading ? (
            <Spinner size={4} className="tw-border-white" />
          ) : (
            "Create Collection"
          )}
        </Button>
      </form>
    </div>
  );
};

export default CreateCollectionModal;
